import styled from '@emotion/styled';
import { useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useForm } from 'react-hook-form';
import PhoneInput from 'views/ui/form_elements/input/phone';
import { array, boolean, date, number, InferType, object, string } from 'yup';
import { BaseModal } from '@anyroad/modal';
import { Icon } from '@anyroad/icon';
import { Spinner } from '@anyroad/spinner';
import { yupResolver } from '@hookform/resolvers/yup';
import { SlotContainter } from './Slots';
import {
  Link,
  CheckboxButton,
  CheckboxContainer,
  CheckboxText,
  Form,
  Header,
  SubHeader,
  InputField,
  NameContainer,
  SpinnerContainer,
  StyledLabel,
  PhoneContainer,
  JoinWaitlistButton,
  CheckboxWrapper,
} from './styles';

type JoinWaitlistContentProps = {
  experienceId: number;
  onClose: () => void;
  showSuccessModal: () => void;
  userId: number;
  brandColor: string;
  isoTime: boolean;
};

const JoinWaitlistContent = ({
  experienceId,
  onClose,
  showSuccessModal,
  userId,
  brandColor,
  isoTime,
}: JoinWaitlistContentProps) => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);

  const validationSchema = object({
    agreedToWaiver: boolean().oneOf([true], 'You must agree to the waiver'),
    firstname: string().required(),
    lastname: string().required(),
    phoneNumber: string(),
    bookingSlotIds: array(),
    slots: array()
      .of(
        object().shape({
          selectedDate: date().required(),
          slotId: number().required(),
        })
      )
      .required(),
    email: string().email().required({
      defaultMessage: 'Email is required',
      description: 'Email is required',
    }),
  });

  const {
    clearErrors,
    formState: { errors },
    register,
    handleSubmit,
    setValue,
    watch,
  } = useForm<InferType<typeof validationSchema>>({
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (data: any) => {
    const customerFacingURL = (window as any).integrationScriptData
      ?.parentLocation?.href;
    const localURL = window.location.href;
    const payload = {
      firstname: data.firstname,
      lastname: data.lastname,
      email: data.email,
      phone_number: data.phoneNumber,
      agreed_to_waiver: data.agreedToWaiver,
      booking_slot_ids: [...new Set(data.bookingSlotIds)],
      origin_url: localURL,
      customer_facing_experience_url: customerFacingURL || null,
    };
    const requestBody = {
      ...payload,
      user_id: userId,
    };
    setLoading(true);
    const response: any = await fetch(
      `/experiences/experiences/${experienceId}/waitlists`,
      {
        method: 'POST',
        body: JSON.stringify(requestBody),
      }
    );
    if (response.ok) {
      showSuccessModal();
      onClose();
    }
  };

  const onSubmitHandler = () => {
    handleSubmit(onSubmit)();
    clearErrors('slots');
  };

  const slotIds = watch('bookingSlotIds') || [];

  const handleAddValue = (slotId: number) => {
    setValue('bookingSlotIds', [...slotIds, slotId]);
  };

  const _phoneInputProps = () => {
    return {
      type: 'tel',
      name: 'phoneNumber',
      value: watch('phoneNumber'),
      onChange: (_name: string, value: string) => {
        setValue('phoneNumber', value);
      },
    };
  };

  return loading ? (
    <SpinnerContainer>
      <Spinner />
    </SpinnerContainer>
  ) : (
    <Form>
      <Header>
        <FormattedMessage
          id="JOIN_WAITLIST"
          defaultMessage="Join waitlist"
          description="Header text for the waitlist signup modal"
        />
      </Header>
      <SubHeader>
        <FormattedMessage
          defaultMessage="Let us know the date and times you're interested in, and if a time becomes available, we'll let you know."
          description="Explanatory text telling users how the waitlist works"
        />
      </SubHeader>
      <SlotContainter
        errors={errors}
        experienceId={experienceId}
        handleAddValue={handleAddValue}
        setValue={setValue}
        brandColor={brandColor}
        isoTime={isoTime}
      />
      <NameContainer>
        <InputField
          labelComponent={
            <StyledLabel error={Boolean(errors.firstname)}>
              <FormattedMessage
                defaultMessage="Name"
                description="Label for the name input field"
              />
            </StyledLabel>
          }
          placeholder={intl.formatMessage({
            defaultMessage: 'First',
            description: 'Placeholder text for first name input field',
          })}
          showError={Boolean(errors.firstname)}
          width="48%"
          {...register('firstname')}
        />
        <InputField
          placeholder={intl.formatMessage({
            defaultMessage: 'Last',
            description: 'Placeholder text for last name input field',
          })}
          showError={Boolean(errors.lastname)}
          width="48%"
          {...register('lastname')}
        />
      </NameContainer>
      <InputField
        labelComponent={
          <StyledLabel error={Boolean(errors.email)}>
            <FormattedMessage
              id="EMAIL_ADDRESS"
              defaultMessage="Email address"
              description="Label for the email address input field"
            />
          </StyledLabel>
        }
        placeholder={intl.formatMessage({
          defaultMessage: 'Email address',
          description: 'Placeholder text for email address input field',
        })}
        showError={Boolean(errors.email)}
        width="100%"
        {...register('email')}
      />
      <PhoneContainer>
        <StyledLabel error={false}>
          <FormattedMessage
            id="PHONE_NUMBER_OPTIONAL"
            defaultMessage="Phone number (Optional)"
            description="Label for the optional phone number input field"
          />
        </StyledLabel>
        <PhoneInput {..._phoneInputProps()} />
      </PhoneContainer>
      <CheckboxContainer showError={Boolean(errors.agreedToWaiver)}>
        <CheckboxWrapper>
          <CheckboxButton
            {...register('agreedToWaiver')}
            className={`brand-color ${
              watch('agreedToWaiver') ? 'brand-bg' : ''
            }`}
            defaultChecked={false}
            type="checkbox"
          />
          <Icon name="Checkmark" style={{ position: 'absolute' }} />
        </CheckboxWrapper>
        <CheckboxText>
          <FormattedMessage
            defaultMessage="By joining the waitlist, you agree to our <termsLink>Terms of Service</termsLink> and to receive communications about your waitlisted event in accordance with our <privacyLink>Privacy Policy</privacyLink>."
            description="Legal agreement text with links to Terms of Service and Privacy Policy"
            values={{
              termsLink: (chunks) => (
                <Link
                  className="brand-color"
                  href="https://www.anyroad.com/end-user-terms"
                  target="_blank"
                >
                  {chunks}
                </Link>
              ),
              privacyLink: (chunks) => (
                <Link
                  className="brand-color"
                  href="https://www.anyroad.com/privacy"
                  target="_blank"
                >
                  {chunks}
                </Link>
              ),
            }}
          />
        </CheckboxText>
      </CheckboxContainer>
      <JoinWaitlistButton
        data-testid="join-waitlist-btn"
        className="brand-bg brand-border"
        color="primary"
        onClick={onSubmitHandler}
        type="button"
      >
        <FormattedMessage
          id="JOIN_WAITLIST"
          defaultMessage="Join waitlist"
          description="Submit button text to join the waitlist"
        />
      </JoinWaitlistButton>
    </Form>
  );
};

export const JoinWaitlist = BaseModal<JoinWaitlistContentProps>(
  JoinWaitlistContent,
  {
    isCloseButtonHidden: false,
    padding: '32px',
    width: '600px',
  }
);

export const JoinWaitlistModal = styled(JoinWaitlist)<JoinWaitlistContentProps>`
  @media (max-width: 768px) {
    padding: 16px;
  }
`;
